import { useCallback } from 'react';
import { useLocation } from 'contexts/LocationContext';
import { useKeyDown } from 'hooks';
import { Chart, Coin, HouseDark, Invoice, LocationPinLocked, Tick } from 'svg';
import { Location } from 'types';
import { formatToProductName } from 'utils/dashboard';
import { removePrefix } from 'utils/misc';
import { formatNumber } from 'utils/number';

type LocationPanelProps = {
  onClose: () => void;
};

function NoDataView() {
  return (
    <div className="flex flex-col items-center text-center p-4">
      <LocationPinLocked />
      <p className="mt-2">
        <span className="font-bold">
          There is not enough data for this location
        </span>
        &nbsp;so the details are hidden to protect customer&apos;s personal
        information
      </p>
    </div>
  );
}

export default function LocationPanel({ onClose }: LocationPanelProps) {
  const [location] = useLocation();
  const showData = Number(location?.numSettled) > 0;

  useKeyDown(() => onClose(), 'Escape');

  const getMostPopularProduct = useCallback(() => {
    const prefix = 'numSettled';
    let maxValue = -Infinity;
    let mostPopularProduct = '';

    for (const key in location) {
      // ignore `numSettled` and `numSettledAll`
      if (
        key.startsWith(prefix) &&
        key !== prefix &&
        !key.startsWith(`${prefix}All`)
      ) {
        const value = location[key as keyof Location];
        if (typeof value === 'number' && !isNaN(value) && value > maxValue) {
          maxValue = value;
          mostPopularProduct = removePrefix(key, prefix);
        }
      }
    }

    return formatToProductName(mostPopularProduct);
  }, [location]);

  const getFunFacts = () => (
    <div className="space-y-4 sm:p-4">
      {location?.numSettled && (
        <div className="iconned-row flexrow">
          <Tick />
          <p className="text-sm">
            <strong>{location?.numSettled} upgrades </strong>
            completed to date.
          </p>
        </div>
      )}

      <div className="iconned-row flexrow break-all">
        <Chart />
        <p className="text-sm">
          <strong>{getMostPopularProduct()}</strong> is the
          <strong> most popular</strong>
          &nbsp;product.
        </p>
      </div>

      {location?.avgAmountSettled && (
        <div className="iconned-row flexrow">
          <Coin />
          <p className="text-sm font-bold">
            Average loan is&nbsp;
            {formatNumber(location?.avgAmountSettled || 0, 'currency')}.
          </p>
        </div>
      )}

      {location?.amountSettled && (
        <div className="iconned-row flexrow">
          <Invoice />
          <p className="text-sm">
            <strong>
              {formatNumber(location?.amountSettled || 0, 'currency')}&nbsp;
            </strong>
            has been accessed so far.
          </p>
        </div>
      )}

      {location?.numDistinctAddressSettled && (
        <div className="iconned-row flexrow">
          <HouseDark />
          <p className="text-sm">
            <strong>
              {location?.numDistinctAddressSettled} homes and community groups
            </strong>
            &nbsp;have accessed the scheme.
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div
      id="map-info-panel-location-panel"
      role="dialog"
      aria-labelledby="map-info-panel-location-heading"
    >
      {/* Web display */}
      <div>
        <div className="map-info-panel">
          <div className="bg-grayMuted p-4">
            <a
              href="javascript:void(0);"
              className="text-xs font-bold text-end underline underline-offset-2"
              onClick={onClose}
            >
              Close
            </a>
            <h4 id="map-info-panel-location-heading" className="h5 mt-4">
              {location?.location} {location?.postcode}
            </h4>
          </div>
          {showData ? (
            <>
              {getFunFacts()}
              <div className="bg-[#0B7951] py-2 mx-4">
                <a
                  className="block text-xs text-center text-white"
                  href="#total-products"
                  onClick={() => {
                    document.location.href = '#';
                    document.location.href = '#total-products';
                  }}
                >
                  See full breakdown
                </a>
              </div>
            </>
          ) : (
            <NoDataView />
          )}
        </div>
      </div>

      {/* Mobile display */}
      <div className="sm:hidden">
        <div className="font-bold my-4">
          {location?.location} {location?.postcode}
        </div>
        {showData ? getFunFacts() : <NoDataView />}
      </div>
    </div>
  );
}
